import { ReactNode, useEffect, useLayoutEffect, useRef, useState } from 'react';

import { motion } from 'framer-motion';

import { Icon } from '@/new/components/icon';
import { cn } from '@/new/lib/utils';

const MIN_WIDTH_PCT = 0.2;
const DEFAULT_WIDTH_PCT = 0.35;
const CONTENT_MIN_WIDTH_PX = 512;
const PANEL_REVEAL_EASE = [0.22, 1, 0.36, 1] as const;

const FALLBACK_CONTAINER_WIDTH = typeof window !== 'undefined' ? window.innerWidth : 1200;
const getWidthFromPct = (containerWidth: number, pct: number) => Math.round(containerWidth * pct);
const getMinPanelWidth = (containerWidth: number) => getWidthFromPct(containerWidth, MIN_WIDTH_PCT);
const getMaxPanelWidth = (containerWidth: number) => containerWidth;
const getInlineMaxPanelWidth = (containerWidth: number) => Math.max(containerWidth - CONTENT_MIN_WIDTH_PX, 0);
const clampPanelWidth = (widthPx: number, containerWidth: number, allowDrawerMode: boolean) => {
  // When drawer mode is off, cap the panel at the inline max so it can never be
  // dragged wider than the content boundary (and so never overlays the content).
  const maxWidth = allowDrawerMode ? getMaxPanelWidth(containerWidth) : getInlineMaxPanelWidth(containerWidth);
  // On narrow containers the inline cap can fall below the minimum width; never
  // let min exceed max, otherwise just fill whatever width is available.
  const minWidth = Math.min(getMinPanelWidth(containerWidth), maxWidth);
  return Math.min(Math.max(widthPx, minWidth), maxWidth);
};

// Persist last width per panel across open/close cycles, keyed so independent
// panels (e.g. Kim references vs. timeline source) don't share a width.
const lastPanelWidthByKey = new Map<string, number>();

interface ResizableSidePanelProps {
  /** Distinct key so each panel remembers its own width across open/close. */
  storageKey: string;
  onClose: () => void;
  /** Extra classes for the inner container (e.g. border sides, rounding). */
  className?: string;
  /**
   * When true, the panel stops resizing at the content boundary and never
   * expands over the content as a drawer. Defaults to false (drawer allowed).
   */
  disableDrawerMode?: boolean;
  children: ReactNode;
}

/**
 * A left-anchored side panel that the user can drag to resize on large screens.
 * The panel sits in flow (as a flex sibling) so the main content shrinks when it
 * opens, and once the content hits its minimum width the panel keeps expanding
 * over the content like a drawer (unless `disableDrawerMode` is set, in which
 * case it stops at the content boundary). Render the panel's header/body as
 * children.
 *
 * Must be rendered inside an <AnimatePresence> so the open/close reveal and
 * drawer overlay animate correctly.
 */
export function ResizableSidePanel({
  storageKey,
  onClose,
  className,
  disableDrawerMode = false,
  children,
}: ResizableSidePanelProps) {
  const allowDrawerMode = !disableDrawerMode;

  const [widthPx, setWidthPx] = useState(() => {
    const rememberedWidth =
      lastPanelWidthByKey.get(storageKey) ?? getWidthFromPct(FALLBACK_CONTAINER_WIDTH, DEFAULT_WIDTH_PCT);
    return clampPanelWidth(rememberedWidth, FALLBACK_CONTAINER_WIDTH, false);
  });
  const [containerWidth, setContainerWidth] = useState(FALLBACK_CONTAINER_WIDTH);
  const [viewportWidth, setViewportWidth] = useState(FALLBACK_CONTAINER_WIDTH);
  const [isResizing, setIsResizing] = useState(false);
  const [isContainerAdjusting, setIsContainerAdjusting] = useState(false);
  const panelRef = useRef<HTMLDivElement>(null);
  const hasMeasuredContainerRef = useRef(false);
  const measuredContainerWidthRef = useRef(FALLBACK_CONTAINER_WIDTH);
  const containerAdjustFrameRef = useRef<number | null>(null);
  const dragOriginRef = useRef<{ clientX: number; widthPx: number } | null>(null);

  const persistWidth = (width: number) => {
    lastPanelWidthByKey.set(storageKey, width);
  };

  const handleMouseDown = (e: React.MouseEvent) => {
    e.preventDefault();
    dragOriginRef.current = { clientX: e.clientX, widthPx };
    setIsResizing(true);
  };

  useLayoutEffect(() => {
    const container = panelRef.current?.parentElement;
    if (!container) return;

    const syncToContainer = () => {
      const nextContainerWidth = Math.round(container.getBoundingClientRect().width);
      const nextViewportWidth = window.innerWidth;

      if (hasMeasuredContainerRef.current && nextContainerWidth !== measuredContainerWidthRef.current) {
        setIsContainerAdjusting(true);
        if (containerAdjustFrameRef.current) {
          cancelAnimationFrame(containerAdjustFrameRef.current);
        }
        containerAdjustFrameRef.current = requestAnimationFrame(() => {
          setIsContainerAdjusting(false);
          containerAdjustFrameRef.current = null;
        });
      }

      measuredContainerWidthRef.current = nextContainerWidth;
      setContainerWidth(nextContainerWidth);
      setViewportWidth(nextViewportWidth);
      const allowDrawerForMeasurement = allowDrawerMode && hasMeasuredContainerRef.current;
      setWidthPx((currentWidth) => {
        // A remembered drawer width can only be classified correctly once the
        // real parent width is known. Cap it at the inline boundary on this
        // first measurement so the panel always opens before the content dims;
        // subsequent drag resizing can still enter drawer mode as normal.
        const constrainedWidth = clampPanelWidth(currentWidth, nextContainerWidth, allowDrawerForMeasurement);
        persistWidth(constrainedWidth);
        return constrainedWidth;
      });
      hasMeasuredContainerRef.current = true;
    };

    syncToContainer();

    const resizeObserver = new ResizeObserver(syncToContainer);
    resizeObserver.observe(container);

    return () => {
      resizeObserver.disconnect();
      if (containerAdjustFrameRef.current) {
        cancelAnimationFrame(containerAdjustFrameRef.current);
      }
    };
  }, []);

  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      if (!isResizing || !dragOriginRef.current) return;

      const { clientX: startX, widthPx: startWidth } = dragOriginRef.current;
      const newPx = startWidth + (startX - e.clientX);
      const constrainedPx = clampPanelWidth(newPx, containerWidth, allowDrawerMode);
      persistWidth(constrainedPx);
      setWidthPx(constrainedPx);
    };

    const handleMouseUp = () => {
      dragOriginRef.current = null;
      setIsResizing(false);
    };

    if (isResizing) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
    }

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isResizing, containerWidth, allowDrawerMode]);

  // Once the main content hits its minimum width, keep the in-flow panel width
  // capped and let the panel continue expanding over the content like a drawer.
  const reservedWidth = Math.min(widthPx, getInlineMaxPanelWidth(containerWidth));
  const isDrawerMode = widthPx > reservedWidth;
  const drawerExitWidth = getInlineMaxPanelWidth(containerWidth);
  const shouldDisableTransitions = isResizing || isContainerAdjusting;
  const overlayOpacityDuration = isContainerAdjusting ? 0 : 0.2;
  const overlayLeftInset = -Math.max(viewportWidth - reservedWidth, 0);
  const overlayRightInset = isDrawerMode ? 0 : widthPx;

  const handleOverlayClick = () => {
    if (!isDrawerMode) return;
    if (drawerExitWidth <= 0) {
      onClose();
      return;
    }
    persistWidth(drawerExitWidth);
    setWidthPx(drawerExitWidth);
  };

  return (
    <motion.div
      ref={panelRef}
      initial={{ width: 0, overflow: isDrawerMode ? 'visible' : 'hidden' }}
      animate={{
        width: reservedWidth,
        overflow: isDrawerMode ? 'visible' : 'hidden',
        transitionEnd: { overflow: 'visible' },
      }}
      exit={{ width: 0, overflow: isDrawerMode ? 'visible' : 'hidden' }}
      transition={{ type: 'tween', duration: shouldDisableTransitions ? 0 : 0.3, ease: PANEL_REVEAL_EASE }}
      className="relative h-full shrink-0"
    >
      <motion.div
        aria-hidden="true"
        initial={{ left: -viewportWidth, right: 0, opacity: isDrawerMode ? 1 : 0 }}
        animate={{ left: overlayLeftInset, opacity: isDrawerMode ? 1 : 0, right: overlayRightInset }}
        exit={{ left: -viewportWidth, right: 0, opacity: 0 }}
        transition={{
          type: 'tween',
          left: { duration: shouldDisableTransitions ? 0 : 0.15 },
          opacity: { duration: overlayOpacityDuration },
          right: { duration: shouldDisableTransitions ? 0 : 0.15 },
        }}
        className={cn(
          'absolute inset-y-0 z-10 bg-black/7 transition-colors duration-150',
          isDrawerMode ? 'pointer-events-auto cursor-pointer' : 'pointer-events-none',
          !isResizing && 'hover:bg-black/3',
        )}
        onClick={isDrawerMode ? handleOverlayClick : undefined}
      />

      <motion.div
        initial={{ clipPath: isDrawerMode ? 'inset(0 0 0 100%)' : 'inset(0 0 0 0%)' }}
        animate={{ clipPath: 'inset(0 0 0 0%)', transitionEnd: { clipPath: 'none' } }}
        exit={{ clipPath: isDrawerMode ? 'inset(0 0 0 100%)' : 'inset(0 0 0 0%)' }}
        transition={{ type: 'tween', duration: shouldDisableTransitions ? 0 : 0.2, ease: PANEL_REVEAL_EASE }}
        style={{ width: widthPx, left: isDrawerMode ? undefined : 0, right: isDrawerMode ? 0 : undefined }}
        className="absolute top-0 z-30 h-full"
      >
        {/* Resize handle */}
        <div onMouseDown={handleMouseDown} className="absolute top-0 -left-2.5 z-40 h-full w-5 cursor-ew-resize">
          <div
            className={cn(
              'absolute top-1/2 left-1/2 z-20 flex h-7 w-5 -translate-x-1/2 -translate-y-1/2 items-center justify-center gap-0.75 rounded-md border border-gray-300 duration-150 hover:bg-gray-100',
              isResizing ? 'bg-gray-100' : 'bg-white',
            )}
          >
            <Icon className="lucide--chevrons-left-right text-xs text-gray-500" />
          </div>
        </div>

        <div
          className={cn(
            'flex h-full flex-col overflow-hidden bg-white',
            isResizing ? 'border-teal-400' : 'border-gray-200',
            isDrawerMode ? 'shadow-2xl' : 'shadow-none',
            'transition-[border-color,box-shadow] duration-300',
            className,
          )}
        >
          {children}
        </div>
      </motion.div>
    </motion.div>
  );
}
